import { scmpPlus } from "@product/scmp-sdk";
import { type FunctionComponent, type ReactNode, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { TopPicksWidgetItem } from "~/components/article/article-left-hand-side-widgets/top-picks-widget/top-picks-widget-item";
import type { AiTopPicksResponse } from "~/lib/ai-engine/types";
import { getAbsoluteUrl, sortArrayByReferenceOrder } from "~/lib/utils";
import type { topPicksWidgetDisplayArticleQuery } from "~/queries/__generated__/topPicksWidgetDisplayArticleQuery.graphql";

import { useTracking } from "./hooks";
import { Container, Header, SponsoredArticleContainer } from "./styles";

export type Props = {
  aiData: AiTopPicksResponse;
  className?: string;
  maximumHeight: number | undefined;
  slots?: {
    footer?: { estimatedHeight: number; node: ReactNode };
    header?: { estimatedHeight: number; node: ReactNode };
  };
};

export const TopPicksWidgetDisplay: FunctionComponent<Props> = ({
  aiData,
  className,
  maximumHeight,
  slots,
}) => {
  const data = useLazyLoadQuery<topPicksWidgetDisplayArticleQuery>(
    graphql`
      query topPicksWidgetDisplayArticleQuery(
        $entityIds: [String]
        $first: Int!
        $scmpPlusPaywallTypeIds: [String]
      ) {
        articles(
          exclude: { paywallTypeIds: $scmpPlusPaywallTypeIds }
          filter: { entityIds: $entityIds }
          first: $first
        ) {
          edges {
            node {
              ...topPicksWidgetItemArticle
              entityId
              urlAlias
            }
          }
        }
      }
    `,
    {
      entityIds: aiData.results.map(result => result.id) ?? [],
      first: aiData.results.length ?? 0,
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
    },
    { fetchPolicy: "store-or-network" },
  );

  const displayArticles = useMemo(() => {
    if (maximumHeight === undefined) return [];

    const orderedArticles = sortArrayByReferenceOrder({
      data: { array: data.articles.edges ?? [], idGetter: item => item.node.entityId },
      order: { array: aiData.results, idGetter: item => item.id },
    });
    const limitedItemCount = Math.max(
      Math.round(
        (maximumHeight -
          (slots?.header?.estimatedHeight ?? 0) -
          (slots?.footer?.estimatedHeight ?? 0)) /
          250,
      ),
      1,
    );
    return orderedArticles.slice(0, limitedItemCount);
  }, [
    aiData.results,
    data.articles.edges,
    maximumHeight,
    slots?.footer?.estimatedHeight,
    slots?.header?.estimatedHeight,
  ]);

  const aiEngineRecommendId = aiData.rec_id;
  const {
    captureFooterSponsorArticleAdImpressionEventTargetElement,
    captureHeaderSponsorArticleAdImpressionEventTargetElement,
    captureTopPickWidgetImpressionEventTargetElement,
    trackTopPicksWidgetItemClick,
  } = useTracking({
    recommendationId: aiEngineRecommendId,
    topPicksEntityIds: displayArticles.map(({ node }) => node.entityId),
  });

  if (displayArticles.length <= 0) return null;

  return (
    <Container className={className} ref={captureTopPickWidgetImpressionEventTargetElement}>
      <Header>Top Picks</Header>
      <SponsoredArticleContainer ref={captureHeaderSponsorArticleAdImpressionEventTargetElement}>
        {slots?.header?.node}
      </SponsoredArticleContainer>
      {displayArticles.map(({ node }) => (
        <TopPicksWidgetItem
          key={node.entityId}
          recommendationId={aiEngineRecommendId}
          reference={node}
          onClick={() => trackTopPicksWidgetItemClick(node.entityId, getAbsoluteUrl(node.urlAlias))}
        />
      ))}
      <SponsoredArticleContainer ref={captureFooterSponsorArticleAdImpressionEventTargetElement}>
        {slots?.footer?.node}
      </SponsoredArticleContainer>
    </Container>
  );
};

TopPicksWidgetDisplay.displayName = "TopPicksWidgetDisplay";
